import {
  Component,
  forwardRef,
  inject,
  input,
  OnInit,
  signal,
  computed,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';

import { InputComponent } from '../input/input.component';
import { SelectComponent, SelectOption } from '../select/select.component';
import { AppType } from '../../../../core/models/app-types.model'; // Import AppType
import { KeyValue } from '../../../../core/models/app-types.model'; // Import KeyValue interface

@Component({
  selector: 'app-key-value-input',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    SelectComponent
],
  templateUrl: './key-value-input.component.html',
  styleUrls: ['./key-value-input.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KeyValueInputComponent),
      multi: true,
    },
  ],
})
export class KeyValueInputComponent implements ControlValueAccessor, OnInit {
  private readonly fb = inject(FormBuilder);

  // --- Component API: Inputs ---
  // FIX: Accept an array of AppType objects instead of strings
  keys = input<AppType[]>([]);
  valuePlaceholder = input('Value');
  otherKeyPlaceholder = input('Custom Type');
  allowOther = input(true);

  // ADD a computed signal to format keys for the app-select component
  protected selectOptions = computed<SelectOption[]>(() => {
    const options = this.keys().map(
      (key) => ({ value: key.id, label: key.label } as SelectOption)
    );
    if (this.allowOther()) {
      options.push({ value: 'other', label: 'Other...' });
    }
    return options;
  });

  // --- Internal State ---
  protected isOtherSelected = signal(false);
  protected form = this.fb.group({
    key: [''],
    value: [''],
  });

  // --- CVA Methods ---
  onChange: (value: KeyValue | null) => void = () => {};
  onTouched: () => void = () => {};

  ngOnInit(): void {
    // When the internal form changes, propagate the value to the parent.
    this.form.valueChanges.subscribe((value) => {
      if (value.key === 'other') {
        // Don't emit the intermediate 'other' value.
        return;
      }
      this.onChange(
        value.key && value.value ? { key: value.key, value: value.value } : null
      );
    });

    // Handle the "Other" selection logic.
    this.form.get('key')?.valueChanges.subscribe((key) => {
      if (key === 'other') {
        this.isOtherSelected.set(true);
        // When 'Other' is picked, we clear the key so the custom input is empty.
        this.form.get('key')?.patchValue('', { emitEvent: false });
      }
    });
  }

  writeValue(obj: { key: string; value: string } | null): void {
    // FIX: Update check to look for the key's id in the new array structure
    if (
      obj &&
      obj.key &&
      !this.keys().some((k) => k.id === obj.key) &&
      this.allowOther()
    ) {
      this.isOtherSelected.set(true);
    } else {
      this.isOtherSelected.set(false);
    }
    this.form.patchValue(obj || { key: '', value: '' }, { emitEvent: false });
  }

  registerOnChange(fn: (value: KeyValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
}
